import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { describeGroupProducts } from '../utils/priceGroupProducts';
import { formatMaxAlertPrice } from '../utils/maxAlertPrice';

/**
 * Every product a price group covers - picture, name, current price - cheapest first, with the
 * ones at or under the group's price marked (#361). The number someone types should have a
 * visible effect: "5 of 20 are $3,000.00 or less right now."
 */
const GroupProductGrid = ({ series, maxPrice, service }) => {
    const [stocks, setStocks] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setStocks(null);
        setError('');
        service.getSeriesProducts(series)
            .then((items) => { if (!cancelled) { setStocks(items); } })
            .catch((loadError) => { if (!cancelled) { setError(loadError.message || 'Failed to load products'); } });
        return () => { cancelled = true; };
    }, [series, service]);

    if (error) {
        return <Typography variant="caption" color="error">{error}</Typography>;
    }
    if (stocks === null) {
        return <Typography variant="caption" color="text.secondary">Loading the {series} products…</Typography>;
    }

    const view = describeGroupProducts(stocks, maxPrice, series);
    const cap = formatMaxAlertPrice(maxPrice);

    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }} data-testid="group-summary">
                {cap
                    ? <><strong>{view.withinPrice} of {view.total}</strong> are {cap} or less right now.</>
                    : <>{view.total} {series} products. Type a price to see which ones it covers.</>}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(5, 1fr)' }
                }}
            >
                {view.products.map((product) => (
                    <Box
                        key={product.sku}
                        component="a"
                        href={`/skus/${product.sku}`}
                        title={product.fullName}
                        data-within-price={product.withinPrice ? 'true' : 'false'}
                        sx={{
                            display: 'block', textDecoration: 'none', color: 'inherit', p: 1,
                            border: '1px solid', borderRadius: 1.5,
                            borderColor: product.withinPrice ? 'success.main' : 'divider',
                            opacity: cap && !product.withinPrice ? 0.45 : 1
                        }}
                    >
                        <Box sx={{ bgcolor: '#fff', borderRadius: 1, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.75 }}>
                            {product.image && (
                                <Box component="img" src={product.image} alt="" loading="lazy"
                                    sx={{ maxWidth: '88%', maxHeight: 64, objectFit: 'contain' }} />
                            )}
                        </Box>
                        <Typography variant="caption" sx={{
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            lineHeight: 1.25, minHeight: '2.5em', color: 'text.secondary'
                        }}>
                            {product.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            {product.withinPrice && <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main' }} />}
                            <Typography variant="body2" fontWeight="700" color={product.withinPrice ? 'success.main' : 'text.primary'}>
                                {product.price === null ? '—' : formatMaxAlertPrice(product.price)}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default GroupProductGrid;
